import type { RefresherEventDetail } from '@ionic/core/components';
import {
  IonContent,
  IonPage,
  IonSkeletonText,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonRow,
} from '@ionic/react';
import { chevronDownCircleOutline } from 'ionicons/icons';
import { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import axios from '../../axios';
import Header from '../../components/Header';
import NoRecords from '../../components/NoRecords';
import { AppContext } from '../../providers/AppContext';

const ModelHistory: React.FC = () => {
  const isMounted = useRef(true);
  const { t } = useTranslation();
  const { state } = useContext(AppContext);

  const [data, setData] = useState<any>({ dataSet: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [disableInfiniteScroll, setDisableInfiniteScroll] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');

  const getData = (context: any) => {
    isMounted.current = true;
    let url = `/method/preflet_asset.apis.model937.routes.get_model937?limit=${state.maxLength}&start=${
      context.isFiltered ? 0 : currentPage * 5 + 0
    }`;

    if ('search' in context || searchText) {
      if (isMounted.current) setSearchText(context.search || '');
      if (context.search) url += `&search=${context.search || searchText}`;
    }

    const config: any = {
      method: 'get',
      url,
      headers: { Authorization: `Basic ${localStorage.getItem('hyprism_login')}` },
    };

    axios(config)
      .then((res: any) => {
        if (res?.data && res.data?.message && res.data.message?.length > 0) {
          if (context.isFiltered) {
            if (isMounted.current) setData({ dataSet: res.data.message });
          } else {
            const existing = { ...data };
            existing.dataSet.push(...res.data.message);
            if (isMounted.current) setData(existing);
          }
          if (isMounted.current) setDisableInfiniteScroll(res.data.message?.length < state.maxLength);
        } else {
          if (context.search) {
            if (isMounted.current) setData({ dataSet: [] });
          } else {
            const existing = { ...data };
            existing.dataSet.push(...(res.data.message || []));
            if (isMounted.current) setData(existing);
          }
          if (isMounted.current) setDisableInfiniteScroll(true);
        }
      })
      .catch((err: any) => console.log(err))
      .finally(() => {
        if (isMounted.current) {
          setIsLoading(false);
          setCurrentPage(currentPage + 1);
        }
      });
  };

  useEffect(() => {
    getData({});
    return () => { isMounted.current = false; };
  }, []);

  function doRefresh(event: CustomEvent<RefresherEventDetail>) {
    getData({});
    setTimeout(() => { event.detail.complete(); }, 2000);
  }

  function searchNext($event: CustomEvent<void>) {
    getData({});
    ($event.target as HTMLIonInfiniteScrollElement).complete();
  }

  const handleSearch = (value: string) => {
    getData({ search: value, isFiltered: true });
  };

  // Generate a pastel color from a string for avatar pill
  const getColor = (name: string) => {
    const colors = ['#1d508d', '#2a6db5', '#6247aa', '#0ea5e9', '#0d9488', '#d97706'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getInitials = (name: string) => {
    const parts = name?.split('-') || [];
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : name?.slice(0, 2).toUpperCase() || '??';
  };

  return (
    <IonPage>
      <Header enableBackButton='/model-home' title={t('Service.History')} />
      <IonContent style={{ '--background': '#f0f4f8' }}>
        <IonRefresher slot="fixed" onIonRefresh={doRefresh}>
          <IonRefresherContent
            pullingIcon={chevronDownCircleOutline}
            pullingText={t('Utils.pullRefresh')}
            refreshingSpinner="circles"
            refreshingText={t('Utils.refreshing')}
          />
        </IonRefresher>

        {/* ── Hero / Search bar ── */}
        <div style={{
          background: 'linear-gradient(135deg, #1d508d 0%, #2a6db5 100%)',
          padding: '16px 14px 14px',
        }}>
          <p style={{ margin: '0 0 10px', fontSize: '11px', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            Model 937 History
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '10px',
            background: 'rgba(255,255,255,0.15)',
            borderRadius: '12px',
            padding: '8px 12px',
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.8)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={`${t('Utils.filter')}...`}
              style={{
                background: 'transparent', border: 'none', outline: 'none',
                color: '#fff', fontSize: '14px', flex: 1,
              }}
            />
            {searchText && (
              <span onClick={() => handleSearch('')} style={{ color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: '20px', lineHeight: 1 }}>×</span>
            )}
          </div>
        </div>

        {/* ── Skeleton loaders ── */}
        {isLoading && [1, 2, 3, 4, 5, 6].map((el) => (
          <div key={el} style={{ margin: '10px 12px', borderRadius: '16px', background: '#fff', padding: '14px 16px', boxShadow: '0 3px 14px rgba(0,0,0,0.06)', display: 'flex', gap: '12px', alignItems: 'center' }}>
            <IonSkeletonText animated style={{ width: '44px', height: '44px', borderRadius: '12px', flexShrink: 0 } as any} />
            <div style={{ flex: 1 }}>
              <IonSkeletonText animated style={{ width: '55%', height: '13px', borderRadius: '6px', marginBottom: '8px' }} />
              <IonSkeletonText animated style={{ width: '35%', height: '10px', borderRadius: '6px' }} />
            </div>
            <IonSkeletonText animated style={{ width: '60px', height: '26px', borderRadius: '20px' }} />
          </div>
        ))}

        {/* ── Model cards ── */}
        <div style={{ padding: '10px 12px 80px' }}>
          {data.dataSet?.map((item: any, key: any) => {
            const name = item.nome || item.name || '';
            const color = getColor(name);
            return (
              <div
                key={key}
                style={{
                  background: '#fff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 18px rgba(0,0,0,0.07)',
                  marginBottom: '10px',
                  padding: '14px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                }}
              >
                {/* Avatar pill */}
                <div style={{
                  width: '46px', height: '46px', borderRadius: '13px',
                  background: `${color}18`,
                  border: `1.5px solid ${color}30`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <span style={{ fontSize: '14px', fontWeight: 800, color }}>{getInitials(name)}</span>
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#1a2a3a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {name}
                  </p>
                  {item.asset && (
                    <p style={{ margin: '3px 0 0', fontSize: '11px', color: '#6b7b8f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {t('Assets.Details.AID')}: {item.asset}
                    </p>
                  )}
                </div>

                {/* Details link */}
                <Link
                  to={{ pathname: '/model-detail', state: item.name }}
                  style={{ textDecoration: 'none', flexShrink: 0 }}
                >
                  <div style={{
                    background: 'linear-gradient(90deg, #1d508d, #2a6db5)',
                    color: '#fff',
                    borderRadius: '20px',
                    padding: '5px 13px',
                    fontSize: '11px',
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                  }}>
                    {t('Assets.Details.Details')} →
                  </div>
                </Link>
              </div>
            );
          })}

          {/* Empty state */}
          {!isLoading && data.dataSet.length === 0 && (
            <IonRow>
              <NoRecords width="60%" smallText={false} />
            </IonRow>
          )}
        </div>

        <IonInfiniteScroll disabled={disableInfiniteScroll} onIonInfinite={(e: CustomEvent<void>) => searchNext(e)}>
          <IonInfiniteScrollContent loadingSpinner="crescent" loadingText={t('Utils.pleasewait')} />
        </IonInfiniteScroll>
      </IonContent>
    </IonPage>
  );
};

export default ModelHistory;
